import type { Locale } from 'date-fns';
import { isMatch } from 'date-fns';
import { format as formatWithTz, zonedTimeToUtc } from 'date-fns-tz';
import { enUS, enGB, enAU, fr, de, es, it, ja } from 'date-fns/locale';
import { WeatherReportContentV103, WeatherReportDateRange, WeatherReportDaily, WeatherReportItineraryLeg } from './WeatherReport.types';

export const WEATHER_REPORT_SCHEMA_URL = 'https://withjoy.com/assets/public/schemas/weatherReport_v1.0.3.schema.json';

const isV103 = (v: unknown): v is WeatherReportContentV103 => {
  if (!v || typeof v !== 'object') return false;
  const obj = v as Record<string, unknown>;
  return Array.isArray(obj.itinerary);
};

const DEFAULT_TIME_ZONE = 'UTC';
const DATE_KEY_FORMAT = 'yyyy-MM-dd';

const SUPPORTED_LOCALES: Record<string, Locale> = {
  'en-us': enUS,
  en: enUS,
  'en-gb': enGB,
  'en-au': enAU,
  'fr-fr': fr,
  fr,
  'de-de': de,
  de,
  'es-es': es,
  es,
  'it-it': it,
  it,
  'ja-jp': ja,
  ja,
};

const resolveLocale = (localeCode?: string): Locale => {
  if (!localeCode) return enUS;
  const normalized = localeCode.toLowerCase();
  return SUPPORTED_LOCALES[normalized] ?? SUPPORTED_LOCALES[normalized.split('-')[0]] ?? enUS;
};

const buildUtcDateFromIso = (isoDate: string, timeZone: string): Date | null => {
  try {
    return zonedTimeToUtc(`${isoDate}T00:00:00`, timeZone);
  } catch (error) {
    console.error('WeatherReport: unable to convert ISO date to UTC', isoDate, timeZone, error);
    return null;
  }
};

const BASE_DAILY: WeatherReportDaily = {
  date: '2026-01-20',
  summary: 'Sunny',
  highTempC: 27,
  lowTempC: 19,
  precipChance: 0.1,
  humidityPct: 65,
  windSpeedKph: 18,
  uvIndex: 6,
};

const BASE_LEG: WeatherReportItineraryLeg = {
  label: 'City',
  location: {
    name: 'City',
    city: 'City',
    country: 'Country',
    timezone: 'UTC',
    geo: { lat: 0, lon: 0 },
  },
  dateRange: { from: '2026-01-20', to: '2026-01-22' },
  daily: [BASE_DAILY],
  guestHighlights: 'Expect mild days with cooler evenings—pack a light sweater.',
};

const BASE_REPORT: WeatherReportContentV103 = {
  id: '00000000-0000-4000-8000-0000000000FF',
  itinerary: [BASE_LEG],
  guestHighlights: 'Pleasant weather overall. Expect sunny days with mild evenings.',
  hostPlanningNotes: '',
  internalRiskAssessment: '',
};

const cloneDaily = (daily: WeatherReportDaily): WeatherReportDaily => ({ ...daily });

const cloneLeg = (leg: WeatherReportItineraryLeg): WeatherReportItineraryLeg => ({
  ...leg,
  location: leg.location
    ? {
        ...leg.location,
        geo: leg.location.geo ? { ...leg.location.geo } : undefined,
      }
    : undefined,
  dateRange: leg.dateRange ? { ...leg.dateRange } : undefined,
  daily: Array.isArray(leg.daily) ? leg.daily.map(cloneDaily) : [],
});

export const cloneWeatherReportContent = (content: WeatherReportContentV103): WeatherReportContentV103 => ({
  ...content,
  itinerary: Array.isArray(content.itinerary) ? content.itinerary.map(cloneLeg) : [],
});

export const createWeatherReportLegTemplate = (overrides?: Partial<WeatherReportItineraryLeg>): WeatherReportItineraryLeg => {
  const leg = cloneLeg(BASE_LEG);

  if (!overrides) {
    return leg;
  }

  return {
    ...leg,
    ...overrides,
    location: overrides.location
      ? {
          ...leg.location,
          ...overrides.location,
          geo: overrides.location.geo
            ? {
                ...(leg.location?.geo ?? {}),
                ...overrides.location.geo,
              }
            : leg.location?.geo,
        }
      : leg.location,
    dateRange: overrides.dateRange ? { ...leg.dateRange, ...overrides.dateRange } : leg.dateRange,
    daily: overrides.daily ? overrides.daily.map(cloneDaily) : leg.daily,
  };
};

export const createWeatherReportTemplate = (): WeatherReportContentV103 => cloneWeatherReportContent(BASE_REPORT);

export const normalizeWeatherReportContent = (value: unknown, fallback: WeatherReportContentV103 = BASE_REPORT): WeatherReportContentV103 => {
  if (isV103(value)) {
    return cloneWeatherReportContent(value);
  }
  return cloneWeatherReportContent(fallback);
};

export const deriveLegTitle = (leg: WeatherReportItineraryLeg, fallback: string): string => {
  if (leg.label) return leg.label;
  const pieces = [leg.location?.name, leg.location?.city, leg.location?.country].filter(Boolean);
  if (pieces.length) return pieces.join(', ');
  return fallback;
};

export const formatWeatherDateRange = (range: WeatherReportDateRange | undefined, options: { locale: string; timeZone?: string }): string => {
  if (!range?.from || !range?.to) return '';
  try {
    const { locale, timeZone } = options;
    const tz = timeZone ?? DEFAULT_TIME_ZONE;
    const fromDateUtc = buildUtcDateFromIso(range.from, tz);
    const toDateUtc = buildUtcDateFromIso(range.to, tz);
    if (!fromDateUtc || !toDateUtc) {
      return `${range.from} – ${range.to}`;
    }
    const localeConfig = resolveLocale(locale);

    const fromMonthKey = formatWithTz(fromDateUtc, 'yyyy-MM', { timeZone: tz, locale: localeConfig });
    const toMonthKey = formatWithTz(toDateUtc, 'yyyy-MM', { timeZone: tz, locale: localeConfig });

    if (fromMonthKey === toMonthKey) {
      const month = formatWithTz(fromDateUtc, 'LLL', { timeZone: tz, locale: localeConfig });
      const fromDay = formatWithTz(fromDateUtc, 'd', { timeZone: tz, locale: localeConfig });
      const toDay = formatWithTz(toDateUtc, 'd', { timeZone: tz, locale: localeConfig });
      const year = formatWithTz(toDateUtc, 'yyyy', { timeZone: tz, locale: localeConfig });
      return `${month} ${fromDay}–${toDay}, ${year}`;
    }

    const fromLabel = formatWithTz(fromDateUtc, 'PP', { timeZone: tz, locale: localeConfig });
    const toLabel = formatWithTz(toDateUtc, 'PP', { timeZone: tz, locale: localeConfig });
    return `${fromLabel} – ${toLabel}`;
  } catch (error) {
    console.error('WeatherReport: unable to format range', error);
    return `${range.from} – ${range.to}`;
  }
};

export const formatWeatherDayParts = (isoDate: string, options: { locale: string; timeZone?: string }): { dow: string; day: string; month: string } => {
  try {
    const tz = options.timeZone ?? DEFAULT_TIME_ZONE;
    const localeConfig = resolveLocale(options.locale);
    if (!isMatch(isoDate, 'yyyy-MM-dd')) {
      return { dow: '', day: isoDate, month: '' };
    }
    const utcDate = buildUtcDateFromIso(isoDate, tz);
    if (!utcDate) {
      return { dow: '', day: isoDate, month: '' };
    }
    return {
      dow: formatWithTz(utcDate, 'EEE', { timeZone: tz, locale: localeConfig }),
      day: formatWithTz(utcDate, 'd', { timeZone: tz, locale: localeConfig }),
      month: formatWithTz(utcDate, 'LLL', { timeZone: tz, locale: localeConfig }),
    };
  } catch (error) {
    console.error('WeatherReport: unable to format day parts', error);
    return { dow: '', day: isoDate, month: '' };
  }
};

export const isIsoDateToday = (isoDate: string | undefined, options: { timeZone?: string } = {}): boolean => {
  if (!isoDate) return false;
  try {
    const trimmed = isoDate.trim();
    if (!isMatch(trimmed, 'yyyy-MM-dd')) {
      return false;
    }
    const { timeZone } = options;
    const zone = timeZone ?? DEFAULT_TIME_ZONE;
    const targetUtc = buildUtcDateFromIso(trimmed, zone);
    if (!targetUtc) {
      return false;
    }
    const targetKey = formatWithTz(targetUtc, DATE_KEY_FORMAT, { timeZone: zone, locale: enUS });
    const todayKey = formatWithTz(new Date(), DATE_KEY_FORMAT, { timeZone: zone, locale: enUS });
    return targetKey === todayKey;
  } catch (error) {
    console.error('WeatherReport: unable to determine today match for date:', isoDate, error);
    return false;
  }
};

export const computeDailyBounds = (daily: ReadonlyArray<WeatherReportDaily> | undefined): { min: number; max: number } => {
  if (!daily || !daily.length) return { min: 0, max: 0 };
  const highs = daily.map(item => item.highTempC);
  const lows = daily.map(item => item.lowTempC);
  return { min: Math.min(...lows), max: Math.max(...highs) };
};

export const clamp01 = (value: number): number => {
  if (!Number.isFinite(value)) return 0;
  if (value < 0) return 0;
  if (value > 1) return 1;
  return value;
};

type UvCategoryKey = 'low' | 'moderate' | 'high' | 'veryHigh' | 'extreme';

export const getUvCategory = (uv?: number): { key: UvCategoryKey; color: string } => {
  const value = typeof uv === 'number' ? uv : 0;
  if (value <= 2) return { key: 'low', color: '#22c55e' };
  if (value <= 5) return { key: 'moderate', color: '#f59e0b' };
  if (value <= 7) return { key: 'high', color: '#f97316' };
  if (value <= 10) return { key: 'veryHigh', color: '#ef4444' };
  return { key: 'extreme', color: '#8b5cf6' };
};
